import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { digObject } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaign from '@hooks/useCampaign'
import useOrderReconciliation from '@hooks/useOrderReconciliation'

import generateNavigationItems from
  '@sections/Client/packs/Accounting/components/AccountingNavigation/utils/generateNavigationItems'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import OrderReconciliationList from '@components/OrderReconciliationList'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ReconciliationTotals from '@components/ReconciliationTotals'

import { ReconciliationItemModel } from '@models/reconciliationItem'

import OrderReconciliationBlankState from '../OrderReconciliationBlankState'
import OrderReconciliationTable from '../OrderReconciliationTable'
import OrderReconciliationTools from '../OrderReconciliationTools'

type CompareCollectionsParams = {
  selectedIds: number[],
  orderReconciliationItems: ReconciliationItemModel[],
  selectedEntityType: string,
}

const compareCollections = (params: CompareCollectionsParams): boolean => {
  const { selectedIds = [], orderReconciliationItems = [], selectedEntityType } = params

  const targetItems = orderReconciliationItems.map(item => (item.entityType !== 'ProcessingFee'
  && item.itemType === selectedEntityType && !item.invoice_reference ? item.id : -1))

  const filterNullItems = targetItems.filter(id => id !== -1)

  const hasSameIds = selectedIds.length === filterNullItems.length

  return hasSameIds
}

type BuildPageFilterFieldsParams = {
  campaignSelected?: boolean,
  providerSelected?: boolean,
  selectedAgencyId?: number,
  setState: (arg: object) => void,
}

const buildPageFilterFields = (params: BuildPageFilterFieldsParams) => {
  const {
    campaignSelected, providerSelected, selectedAgencyId, setState,
  } = params

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      callbacks: {
        onChange: (value: number) => setState({ providerSelected: !!value }),
        buildSearchRequestOptions: () => ({ assigned_agency_id: selectedAgencyId }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        disabled: !!campaignSelected,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
    {
      callbacks: {
        onChange: (value: number) => setState({ campaignSelected: !!value }),
        buildSearchRequestOptions: () => ({ agency_id: selectedAgencyId }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        disabled: !!providerSelected,
        entityKey: 'campaigns',
        requestOptions: { include_campaign_reconciliation_totals: true },
      },
      key: 'campaign_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      hidden: !providerSelected,
      key: 'approval_date_start',
      label: 'Approval Date Start',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      hidden: !providerSelected,
      key: 'approval_date_end',
      label: 'Approval Date End',
    },
  ]
}

export type DefaultState = {
  campaignSelected: boolean,
  editMode: boolean,
  selectedEntityType: string,
  providerSelected: boolean,
  viewMode: 'list' | 'table',
}

const defaultState = {
  campaignSelected: false,
  editMode: false,
  selectedEntityType: '',
  providerSelected: false,
  viewMode: 'table',
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const {
    approval_date_end: endDate,
    approval_date_start: startDate,
    campaign_id: selectedCampaignId,
    provider_id: selectedProviderId,
  } = pageFilters || {}

  const [state, setState] = useSetState(defaultState)
  const {
    campaignSelected, editMode, providerSelected, selectedEntityType, viewMode,
  } = state

  const { selectedAgencyId } = useAgencySelector()

  // @ts-expect-error TS2345
  const nav = generateNavigationItems(selectedAgencyId)
  const navItems = digObject(nav.sections[0], 'items')

  const { campaign, loading: loadingCampaign } = useCampaign({ id: selectedCampaignId })

  const orderReconciliationPayload = useOrderReconciliation({
    agency_id: selectedAgencyId,
    ...pageFilters,
    orderStatusGroupKey: 'agency_orders_not_pending',
  })

  const {
    loadingOrderReconciliationItems,
    orderReconciliationItems,
    selectedEntityIds,
  } = orderReconciliationPayload

  const allSelected = compareCollections({
    // @ts-expect-error TS2322
    orderReconciliationItems: orderReconciliationItems?.items,
    selectedEntityType,
    selectedIds: selectedEntityIds,
  })

  const checkHasReconciliationItems = (orderReconciliationItems.items.length > 1
    && orderReconciliationPayload.hasReconciliationItems && orderReconciliationItems.totals.expectedTotal !== 0)

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const showOrderReconciliation = (selectedCampaignId || selectedProviderId)

  const buildBlankStateParagraph = `There are no matching orders for this ${
    selectedCampaignId ? 'Campaign' : 'Provider'
  }. Please try adjusting your filters.`

  return (
    <>
      <Helmet>
        <title>Order Reconciliation | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="order-reconciliation"
        nestedNavigation
        tabBarItems={navItems}
        title="Order Reconciliation"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.orderReconciliation.banner" />

            <LoadingModule loading={loadingOrderReconciliationItems} times={3} />

            {!loadingOrderReconciliationItems && !selectedCampaignId && !selectedProviderId && (
              <OrderReconciliationBlankState
                paragraph="Please start by applying a filter of a provider
                or a campaign to see a list of orders that require reconciling."
              />
            )}

            {!loadingCampaign && showOrderReconciliation
             && !loadingOrderReconciliationItems && viewMode === 'list' && checkHasReconciliationItems && (
             <OrderReconciliationList
               campaign={campaign}
               orderReconciliationPayload={orderReconciliationPayload}
               providerSelected={!!selectedProviderId}
               title="Orders"
             />
            )}

            {!loadingCampaign && showOrderReconciliation
            && !loadingOrderReconciliationItems && viewMode === 'table' && checkHasReconciliationItems && (
              <OrderReconciliationTable
                agencyId={selectedAgencyId}
                callbacks={{ setState }}
                campaign={campaign}
                dates={{ startDate, endDate }}
                editMode={editMode}
                orderReconciliationPayload={orderReconciliationPayload}
                providerId={selectedProviderId}
                selectedEntityType={selectedEntityType}
                selectedProviderId={selectedProviderId}
              />
            )}

            {!loadingCampaign && showOrderReconciliation
            && !loadingOrderReconciliationItems && !checkHasReconciliationItems
            && (viewMode === 'table' || viewMode === 'list') && (
              <OrderReconciliationBlankState
                paragraph={buildBlankStateParagraph}
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.orderReconciliation.sidebar" />

            {showOrderReconciliation && (
              <OrderReconciliationTools
                allSelected={allSelected}
                callbacks={{
                  setState,
                }}
                checkHasReconciliationItems={checkHasReconciliationItems}
                editMode={editMode}
                orderReconciliationPayload={orderReconciliationPayload}
                selectedCampaignId={selectedCampaignId}
                selectedEntityType={selectedEntityType}
                selectedProviderId={selectedProviderId}
                viewMode={viewMode}
              />
            )}

            <PageFilterModule
              callbacks={{
                resetFilters: (resetFn?: () => void) => {
                  resetFilters(resetFn)
                  setState({ providerSelected: false, campaignSelected: false, selectedEntityType: '' })
                },
                updateFilters,
              }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields({
                // @ts-expect-error TS2353
                campaignSelected, providerSelected, selectedAgencyId, setState, updateFilters,
              })}
              pageFilters={pageFilters}
              title="Filters"
            />

            {selectedCampaignId && (
              <ReconciliationTotals campaign={campaign} />
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
